import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStatus,
  type Placement,
} from '@floating-ui/react';
import { cloneElement, isValidElement, useState, type ReactElement, type ReactNode, type Ref } from 'react';
import { Kbd } from './Kbd.tsx';
import './overlay.css';

export interface TooltipProps {
  content: ReactNode;
  /** Optional shortcut shown after the label, e.g. `mod+k`. */
  shortcut?: string;
  placement?: Placement;
  delay?: number;
  disabled?: boolean;
  /** Must be a single element that forwards `ref` to a DOM node. */
  children: ReactElement;
}

export function Tooltip({ content, shortcut, placement = 'top', delay = 450, disabled, children }: TooltipProps) {
  const [open, setOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    // top/left positioning leaves `transform` free for the enter/exit animation
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [offset(7), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const hover = useHover(context, { delay: { open: delay, close: 0 }, move: false, enabled: !disabled });
  const focus = useFocus(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);
  const { isMounted, status } = useTransitionStatus(context, { duration: { open: 140, close: 90 } });

  const childProps = (isValidElement(children) ? children.props : {}) as Record<string, unknown> & { ref?: Ref<Element> };
  const ref = useMergeRefs([refs.setReference, childProps.ref]);

  if (!content || disabled) return children;

  return (
    <>
      {cloneElement(children, getReferenceProps({ ...childProps, ref } as Record<string, unknown>))}
      {isMounted && (
        <FloatingPortal>
          <div ref={refs.setFloating} className="g-tooltip" data-status={status} style={floatingStyles} {...getFloatingProps()}>
            <span>{content}</span>
            {shortcut && <Kbd shortcut={shortcut} size="sm" className="g-tooltip__kbd" />}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
